import {
  EntityId,
  EntitySelectEntity,
  EntitySelectSubEntity
} from '@factorialco/f0-react/dist/experimental'

export interface TrainingClass {
  id: string
  name: string
  startDate?: string | null
  endDate?: string | null
  mode: string
  grossCost: string
  currency: string
}

export interface Training {
  id: number
  name: string
  trainingClassesConnection: {
    nodes: TrainingClass[]
  }
}

/**
 * Transform trainings with classes into EntitySelectEntity format for EntitySelectWrapper.
 * Trainings are parent entities, training classes are subItems.
 */
export function mapTrainingsToEntities(
  trainings: Training[],
  expandedElements: EntityId[] = []
): EntitySelectEntity[] {
  return trainings.map((training) => ({
    id: training.id,
    name: training.name,
    expanded: expandedElements.includes(training.id),
    subItems: training.trainingClassesConnection.nodes.map(
      (cls): EntitySelectSubEntity => ({
        subId: cls.id,
        subName: cls.name,
        subSearchKeys: [cls.name, training.name]
      })
    )
  }))
}

/**
 * Get selected entities filtered by selected class IDs.
 * Used to display currently selected items in EntitySelectWrapper.
 */
export function getSelectedEntities(
  allEntities: EntitySelectEntity[],
  selectedClassIds: string[]
): EntitySelectEntity[] {
  return allEntities.reduce<EntitySelectEntity[]>((acc, entity) => {
    const filteredSubItems = entity.subItems?.filter((subItem) =>
      selectedClassIds.includes(String(subItem.subId))
    )

    if (filteredSubItems?.length) {
      acc.push({ ...entity, subItems: filteredSubItems })
    }

    return acc
  }, [])
}

/**
 * Calculate total allocation in cents from selected training classes.
 * Sums grossCost (decimal string) for all selected classes.
 */
export function calculateAllocationCents(
  trainings: Training[],
  selectedClassIds: string[]
): number {
  let totalCents = 0

  trainings.forEach((training) => {
    training.trainingClassesConnection.nodes.forEach((cls) => {
      if (selectedClassIds.includes(cls.id)) {
        // grossCost is a string representing the amount (not cents)
        // Convert to cents for consistent handling
        const costValue = parseFloat(cls.grossCost) || 0
        totalCents += Math.round(costValue * 100)
      }
    })
  })

  return totalCents
}
